import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import { alpha, useTheme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { FC, ReactNode, memo } from 'react';
import { MdCheck, MdHelpOutline, MdPalette } from 'react-icons/md';

import { STEPS } from './steps';

const HELP_PDF_URL =
  'https://assets.restoreyourcore.com/pro-directory/working-with-the-pro-directory-profile-form-v1.0.pdf';
const BRAND_GUIDELINES_PDF_URL =
  'https://media.restoreyourcore.com/uploads/2026-06-17/RYC%C2%AE%20Certified%20Teacher%20_%20Professional%20Brand%20Guidelines%20&%20Logos-1781681874282.pdf';

const HeaderPdfLink: FC<{ href: string; tooltip: string; icon: ReactNode; label: string }> = ({
  href,
  tooltip,
  icon,
  label,
}) => (
  <Tooltip title={tooltip}>
    <Link
      href={href}
      target="_blank"
      rel="noopener"
      download
      underline="none"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        flexShrink: 0,
        color: 'text.secondary',
        fontWeight: 500,
        fontSize: theme => theme.typography.caption.fontSize,
        '&:hover': { color: 'primary.main' },
      }}
    >
      {icon}
      <Box component="span" sx={{ display: { xs: 'none', sm: 'block' } }}>
        {label}
      </Box>
    </Link>
  </Tooltip>
);

type Props = {
  activeStep: number;
  completedSteps: boolean[];
  onStepClick: (step: number) => void;
  isStepEnabled: (step: number) => boolean;
};

const StepperHeader: FC<Props> = ({ activeStep, completedSteps, onStepClick, isStepEnabled }) => {
  const theme = useTheme();

  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      justifyContent={{ xs: 'space-between', lg: 'flex-end' }}
      sx={{ width: '100%', flexWrap: 'wrap', rowGap: 1 }}
    >
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mr: { lg: 'auto' }, flexShrink: 0 }}>
        <HeaderPdfLink
          href={HELP_PDF_URL}
          tooltip="Working with the Pro Directory profile form (PDF)"
          icon={<MdHelpOutline size={18} />}
          label="Help"
        />
        <HeaderPdfLink
          href={BRAND_GUIDELINES_PDF_URL}
          tooltip="RYC® Certified Teacher professional brand guidelines & logos (PDF)"
          icon={<MdPalette size={18} />}
          label="Brand Guidelines"
        />
      </Stack>

      {STEPS.map((step, index) => {
        const isActive = index === activeStep;
        const isCompleted = completedSteps[index] && !isActive;
        const isEnabled = isStepEnabled(index);
        const isLast = index === STEPS.length - 1;
        const Icon = step.icon;

        return (
          <Box
            key={step.label}
            sx={{
              display: 'flex',
              alignItems: 'center',
              flex: {
                xs: isLast ? '0 0 auto' : 1,
                lg: '0 0 auto',
              },
              minWidth: 0,
            }}
          >
            <ButtonBase
              onClick={() => onStepClick(index)}
              disabled={!isEnabled}
              aria-label={step.label}
              sx={{
                flexShrink: 0,
                justifyContent: 'flex-start',
                borderRadius: 5,
                bgcolor: isActive ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
                px: { xs: 1, sm: 1.5 },
                py: 1.25,
                gap: { xs: 0, sm: 0.75 },
                opacity: isEnabled ? 1 : 0.5,
                transition: theme.transitions.create(['background-color']),
              }}
            >
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  bgcolor: isActive ? 'primary.main' : alpha(theme.palette.primary.main, isCompleted ? 0.15 : 0.08),
                  color: isActive ? 'primary.contrastText' : isCompleted ? 'primary.main' : 'text.secondary',
                }}
              >
                {isCompleted ? <MdCheck size={14} /> : <Icon size={14} />}
              </Box>
              <Typography
                variant="caption"
                noWrap
                sx={{
                  display: { xs: 'none', sm: 'block' },
                  fontWeight: isActive ? 700 : 500,
                  color: isActive ? 'primary.main' : 'text.secondary',
                }}
              >
                {step.label}
              </Typography>
            </ButtonBase>

            {!isLast && (
              <Box
                sx={{
                  alignSelf: 'center',
                  flex: 1,
                  minWidth: 12,
                  maxHeight: '1px',
                  minHeight: '1px',
                  mx: 0.5,
                  bgcolor: alpha(theme.palette.primary.main, isCompleted || isActive ? 0.4 : 0.15),
                }}
              />
            )}
          </Box>
        );
      })}
    </Stack>
  );
};

/**
 * Rendered inside a form whose parent re-renders on every keystroke (useProDirectoryForm
 * watches all fields). Props are stabilised there, so a shallow compare holds
 * and this only re-renders when the step or its completion state changes.
 */
export const ProDirectoryStepperHeader = memo(StepperHeader);
